/**
 * SegmentedControl — animated tab picker with optional icon per segment.
 *
 * Used in the alarm control panel for hours/minutes toggle.
 *
 * Adaptations from the app's SegmentedSelector:
 * - Icon is accepted as a ReactNode render function per option (no Icon import)
 * - maxWidth is a prop (no app-specific `sizes` constant)
 */
import { type FC, memo, type ReactNode } from "react";
import {
  ActivityIndicator,
  Pressable,
  View,
  useWindowDimensions,
  Platform,
  type StyleProp,
  type ViewStyle,
} from "react-native";
import Animated, { AnimatedStyle, FadeIn, useAnimatedStyle, withTiming } from "react-native-reanimated";
import "../../theme"; // module augmentation for react-native-unistyles types
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import { Text } from "../Text";
import { TextColorPalette } from "../Text";
import { toSentenceCase } from "../../utils/sentence-case";

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export interface SegmentedControlOption {
  readonly title: string;
  /** Optional icon rendered to the left of the title. */
  readonly icon?: ReactNode;
}

export interface SegmentedControlProps {
  readonly options: readonly SegmentedControlOption[];
  readonly selectedOption: string;
  readonly onSelectOption: (option: string) => void;
  readonly width?: number;
  readonly isSelectionVisible?: boolean;
  readonly containerStyle?: StyleProp<ViewStyle>;
  readonly activeBoxStyle?: AnimatedStyle<ViewStyle>;
  /** Max width used on web for responsive sizing. Defaults to 1824. */
  readonly maxWidth?: number;
  readonly textColorActiveBox?: TextColorPalette;
  /** When true, shows a loading overlay that prevents interaction. */
  readonly loading?: boolean;
  /** When true, disables all interaction without showing a loading indicator. */
  readonly disabled?: boolean;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const CONTAINER_HEIGHT = 48;
const DEFAULT_MAX_WIDTH = 1824;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const SegmentedControl: FC<SegmentedControlProps> = memo(
  ({
    options,
    selectedOption,
    onSelectOption,
    width,
    isSelectionVisible = true,
    containerStyle,
    activeBoxStyle,
    maxWidth = DEFAULT_MAX_WIDTH,
    textColorActiveBox = "text",
    loading = false,
    disabled = false,
  }) => {
    const { theme } = useUnistyles();
    const optionsTitles = options.map((o) => o.title);
    const { width: windowWidth } = useWindowDimensions();

    const availableWidth = Platform.OS === "web" ? Math.min(windowWidth * 0.5, maxWidth) : windowWidth * 0.5;
    const segmentedControlWidth = width ?? availableWidth - theme.spacing["5xl"] * 2;
    const selectedItemWidth = options.length > 0 ? segmentedControlWidth / options.length : 0;

    const rStyle = useAnimatedStyle(() => {
      const selectedIndex = optionsTitles.indexOf(selectedOption);
      const isHidden = !isSelectionVisible || selectedIndex < 0;

      const leftPosition = optionsTitles.slice(0, selectedIndex).reduce((acc) => acc + selectedItemWidth, -1);

      return {
        opacity: withTiming(isHidden ? 0 : 1, { duration: 200 }),
        left: withTiming(leftPosition, { duration: 300 }),
        width: withTiming(selectedItemWidth, { duration: 300 }),
      };
    }, [selectedOption, selectedItemWidth, optionsTitles, isSelectionVisible]);

    return (
      <View style={styles.outerContainer}>
        <View style={[styles.container, { width: segmentedControlWidth }, containerStyle]} accessibilityRole="tablist">
          <Animated.View style={[styles.activeBox, { height: "105%", top: -1 }, rStyle, activeBoxStyle]} />
          {options.map((option) => (
            <Pressable
              onPress={() => {
                onSelectOption(option.title);
              }}
              key={option.title}
              style={[styles.row, { width: selectedItemWidth }]}
              disabled={loading || disabled}
              accessibilityRole="tab"
              accessibilityState={{ selected: selectedOption === option.title }}
              accessibilityLabel={toSentenceCase(option.title)}
            >
              <Animated.View style={[styles.textRow, { justifyContent: "center", gap: theme.spacing.md }]}>
                {option.icon !== undefined && option.icon}

                <Animated.View entering={FadeIn.duration(500)}>
                  <Text
                    textStyle="sm/regular"
                    style={styles.title}
                    color={selectedOption === option.title ? textColorActiveBox : "textMuted"}
                  >
                    {toSentenceCase(option.title)}
                  </Text>
                </Animated.View>
              </Animated.View>
            </Pressable>
          ))}
          {loading && (
            <View style={styles.loadingOverlay} pointerEvents="box-only">
              <ActivityIndicator size="small" color={theme.colors.textMuted} />
            </View>
          )}
        </View>
      </View>
    );
  },
);

SegmentedControl.displayName = "SegmentedControl";

export { SegmentedControl };

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const styles = StyleSheet.create((theme) => ({
  outerContainer: {},
  container: {
    flexDirection: "row",
    height: CONTAINER_HEIGHT,
    backgroundColor: theme.colors.surface,
    borderWidth: 1,
    borderColor: theme.colors.border,
    borderRadius: theme.spacing["2xl"],
    borderCurve: "continuous",
    userSelect: "none",
  },
  row: {
    justifyContent: "center",
    alignItems: "center",
    flexDirection: "row",
  },
  activeBox: {
    position: "absolute",
    backgroundColor: theme.colors.foreground,
    borderWidth: 1,
    borderColor: theme.colors.interactive.buttons.default_background,
    borderRadius: theme.spacing["2xl"],
    borderCurve: "continuous",
  },
  textRow: {
    flexDirection: "row",
    alignItems: "center",
  },
  title: {
    textAlign: "center",
  },
  loadingOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: "center",
    alignItems: "center",
    borderRadius: theme.spacing["2xl"],
  },
}));
